'use client';
import { useEffect, useState, useRef } from 'react';

import { StatisticsCardProps } from './types';
import { BlurredCard } from '../../blurredCard';

const ANIMATION_TIME = 2000;

export const StatisticsCard = ({
  number,
  units,
  description,
  step = 1,
  needToStartAnimation = false,
}: StatisticsCardProps) => {
  const statisticRef = useRef<HTMLParagraphElement>(null);
  const [currentNumber, setCurrentNumber] = useState(0);

  const totalSteps = Math.ceil(number / step);
  const interval = Math.floor(ANIMATION_TIME / totalSteps);

  useEffect(() => {
    if (!needToStartAnimation) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentNumber((currentNumber) => {
        if (currentNumber >= number) {
          clearInterval(intervalId);
          return currentNumber;
        }

        const newNumber = Number.isInteger(currentNumber + step)
          ? currentNumber + step
          : Number((currentNumber + step).toFixed(2));

        return newNumber;
      });
    }, interval);

    return () => clearInterval(intervalId);
  }, [number, step, interval, needToStartAnimation]);

  return (
    <BlurredCard className="flex flex-col items-start justify-between p-4 md:p-5 md:pt-[60px]">
      <p
        ref={statisticRef}
        className="mb-[30px] whitespace-nowrap md:mb-2 lg:w-[224px]"
      >
        <span className="text-[2.5rem] leading-[4rem] md:text-[4rem]">
          {Number.isInteger(currentNumber)
            ? currentNumber
            : currentNumber.toString().replace('.', ',')}
          {currentNumber >= number && '+'}
        </span>
        {units && (
          <span className="text-text-base-secondary ml-2 text-2xl leading-[38px] font-medium md:text-4xl">
            {` ${units}`}
          </span>
        )}
      </p>
      <p className="tracking-tight">{description}</p>
    </BlurredCard>
  );
};

export const Statistics = ({ list }: { list: StatisticsCardProps[] }) => {
  const statisticRef = useRef<HTMLDivElement>(null);
  const [isStatisticVisible, setIsStatisticVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      entry.isIntersecting && setIsStatisticVisible(true);
    });

    const statisticBlockRef = statisticRef.current;

    statisticBlockRef && observer.observe(statisticBlockRef);

    return () => {
      statisticBlockRef && observer.unobserve(statisticBlockRef);
    };
  }, []);

  return (
    <div
      ref={statisticRef}
      className="grid grid-cols-2 gap-4 md:w-full md:gap-8"
    >
      {list.map((item, index) => (
        <StatisticsCard
          {...item}
          key={index}
          needToStartAnimation={isStatisticVisible}
        />
      ))}
    </div>
  );
};
